
import { ChangeDetectionStrategy, Component, computed, Input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TwitchComment } from '../models/twitch.model';

// Side-by-side chat column for browsers that enforce Twitch's obscured-iframe
// check (Chrome family, 2026-05-13+). Renders to the right of the player iframe
// in the same flex row — bbox does NOT overlap with the iframe, so the SDK's
// IntersectionObserver V2 trackVisibility check stays green.
//
// Identical comment-filtering logic to chat-overlay (binary search by offset
// window, max items cap). Layout/style differs: fills column height naturally,
// no absolute positioning, no slide-in animation (no DOM-mount churn).

@Component({
  selector: 'app-chat-side',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="h-full w-full flex flex-col bg-gray-900/70 border-l border-gray-800">
      <div class="flex-shrink-0 px-3 py-2 border-b border-gray-800 text-[11px] uppercase tracking-wider text-gray-500 font-semibold">
        Чат стрима
      </div>
      <div class="flex-1 min-h-0 overflow-hidden px-3 pb-3 flex flex-col justify-end gap-1.5 mask-image-gradient">
        @for (comment of visibleComments(); track comment.content_offset_seconds + '-' + comment.commenter.display_name + '-' + comment.message.body) {
          <div class="bg-black/30 text-gray-100 rounded-lg px-2.5 py-1.5 border border-white/5 animate-fade-in-up origin-bottom-right self-stretch max-w-full">
            <p class="text-[10px] font-bold text-indigo-300 mb-0.5 truncate">{{ comment.commenter.display_name }}</p>
            <p class="text-xs break-words leading-snug">{{ comment.message.body }}</p>
          </div>
        }
      </div>
    </div>
  `,
  styles: [`
    .mask-image-gradient {
      mask-image: linear-gradient(to bottom, transparent, black 15%);
      -webkit-mask-image: linear-gradient(to bottom, transparent, black 15%);
    }
    .animate-fade-in-up {
      animation: fadeInUp 0.3s ease-out forwards;
    }
    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(10px) scale(0.95); }
      to   { opacity: 1; transform: translateY(0) scale(1); }
    }
  `]
})
export class ChatSideComponent {
  private commentsSignal = signal<TwitchComment[]>([]);
  private currentTimeSignal = signal<number>(0);

  @Input({ required: true })
  set comments(val: TwitchComment[]) { this.commentsSignal.set(val); }

  @Input({ required: true })
  set currentTime(val: number) { this.currentTimeSignal.set(val); }

  visibleComments = computed(() => {
    const time = this.currentTimeSignal();
    const all = this.commentsSignal();
    if (all.length === 0) return [];

    const windowSize = 15;
    const startTime = Math.max(0, time - windowSize);

    let low = 0, high = all.length - 1, startIndex = -1;
    while (low <= high) {
      const mid = (low + high) >>> 1;
      if (all[mid].content_offset_seconds >= startTime) { startIndex = mid; high = mid - 1; }
      else { low = mid + 1; }
    }
    if (startIndex === -1) return [];

    const result: TwitchComment[] = [];
    const maxItems = 40;
    for (let i = startIndex; i < all.length; i++) {
      const c = all[i];
      if (c.content_offset_seconds > time) break;
      result.push(c);
      if (result.length >= maxItems) return result.slice(-maxItems);
    }
    return result;
  });
}
